import React, { useRef, useState } from 'react';
import { Alert } from '@mui/material';
import { deleteFolderIfEmpty, postCreateFolder } from '../../../api/foldersApi';
import { getContextPins, PinTarget, pinContent, reorderPinnedContent, unpinContent } from '../../../api/pinnedContentApi';
import { beginMoveUndo } from '../../../helpers/moveUndo';
import { VIDEOFEED } from '../../../enums/TYPES';
import Modal from '../Modal';
import { ContainedButton, OutlinedButton } from '../Buttons';
import folderStyles from '../../../hoc/Layout/Sidebar/SiderbarModals/SidebarModals.module.scss';
import styles from './ContentDragDrop.module.scss';

type Props = {
  files: [VIDEOFEED, VIDEOFEED];
  parentFolderId: string;
  move: (file: VIDEOFEED, sourceId: string, destinationId: string) => Promise<unknown>;
  onClose: () => void;
  onUpdated: () => void;
};

const CreateFolderFromDrop: React.FC<Props> = ({ files, parentFolderId, move, onClose, onUpdated }) => {
  const [name, setName] = useState('New folder');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const destinationId = useRef<string | null>(null);
  const movedIds = useRef(new Set<string>());
  const submitting = useRef(false);
  const originalPins = useRef<PinTarget[] | null>(null);
  const showUndo = useRef<ReturnType<typeof beginMoveUndo> | null>(null);

  const offerUndo = () => {
    const createdFolderId = destinationId.current;
    const pins = originalPins.current;
    if (!createdFolderId || !pins) return;
    const movedFiles = files.filter((file) => movedIds.current.has(file.id));
    const message = movedFiles.length === 2
      ? `Created ${name.trim()} and moved both files`
      : `Created ${name.trim()}; ${movedFiles.length} of 2 files moved`;
    showUndo.current?.(message, async () => {
      const context = { contextType: 'videofeeds', contextParentId: parentFolderId };
      for (const file of movedFiles) {
        await move(file, createdFolderId, parentFolderId);
        const payload = { ...context, targetType: 'videofeed' as const, targetId: file.id };
        const wasPinned = pins.some((pin) => pin.targetType === 'videofeed' && String(pin.targetId) === String(file.id));
        await (wasPinned ? pinContent(payload) : unpinContent(payload));
      }
      await reorderPinnedContent({ ...context, items: pins });
      const response = await deleteFolderIfEmpty(createdFolderId);
      return response.data.deleted
        ? 'Move undone and new folder removed'
        : 'Move undone. The new folder was kept because it contains other content.';
    });
  };

  const confirm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || !name.trim()) return;
    submitting.current = true;
    setBusy(true);
    setError('');
    showUndo.current = beginMoveUndo();
    try {
      if (!originalPins.current) originalPins.current = await getContextPins('videofeeds', parentFolderId);
      if (!destinationId.current) {
        const response = await postCreateFolder({ name: name.trim(), parentFolderId });
        destinationId.current = String(response.data.id);
      }
      for (const file of files) {
        if (movedIds.current.has(file.id)) continue;
        await move(file, parentFolderId, destinationId.current);
        movedIds.current.add(file.id);
      }
      onClose();
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Could not move the files. Please try again.';
      setError(destinationId.current
        ? `Folder "${name.trim()}" was created. ${movedIds.current.size} of 2 files moved. Retry to move the remaining files. ${message}`
        : String(message));
    } finally {
      submitting.current = false;
      setBusy(false);
      offerUndo();
      onUpdated();
    }
  };

  return (
    <Modal open onClose={busy ? undefined : onClose} loading={busy}>
      <div className={`${folderStyles.modalContainer} ${styles.modal}`}>
        <div className={folderStyles.modalTitle}>Create a new subfolder?</div>
        <div className={styles.filesSummary}>
          Move these files into the new folder:
          <ul>
            {files.map((file) => (
              <li key={file.id}>{file.collection_name || file.collection_title || file.title}</li>
            ))}
          </ul>
        </div>
        <form className={folderStyles.form} onSubmit={confirm}>
          <input
            className={`${folderStyles.field} ${styles.folderName}`}
            type="text"
            aria-label="Folder name"
            placeholder="Enter Folder Name"
            value={name}
            autoFocus
            disabled={busy || Boolean(destinationId.current)}
            onChange={(event) => setName(event.target.value)}
          />
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <ContainedButton className={folderStyles.customBtn} type="submit" disabled={busy || !name.trim()}>
            {busy ? 'Moving…' : destinationId.current ? 'Retry move' : 'Create & move'}
          </ContainedButton>
          <OutlinedButton kind="default" disabled={busy} onClick={onClose}>
            {destinationId.current ? 'Close' : 'Cancel'}
          </OutlinedButton>
        </form>
      </div>
    </Modal>
  );
};

export default CreateFolderFromDrop;
